import { router } from '@inertiajs/react'
import { useState } from 'react'
import { ArrowLeftIcon } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

interface DriverLead {
  id: number
  name: string
  phone: string | null
  email: string | null
  source: string
  source_id: string | null
  driver_type: string
  cdl_status: string
  years_experience: number | null
  work_authorization: string
  currently_working: string
  status: string
  notes: string | null
}

interface DriverLeadsEditProps {
  driverLead: DriverLead
}

export default function DriverLeadsEdit({ driverLead }: DriverLeadsEditProps) {
  const [formData, setFormData] = useState({
    name: driverLead.name,
    phone: driverLead.phone || '',
    email: driverLead.email || '',
    source: driverLead.source,
    source_id: driverLead.source_id || '',
    driver_type: driverLead.driver_type,
    cdl_status: driverLead.cdl_status,
    years_experience: driverLead.years_experience?.toString() || '0',
    work_authorization: driverLead.work_authorization,
    currently_working: driverLead.currently_working,
    status: driverLead.status,
    notes: driverLead.notes || '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    router.patch(`/driver_leads/${driverLead.id}`, { driver_lead: formData })
  }

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
          {/* Header */}
          <div className="px-4 lg:px-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => router.visit('/driver_leads')}
              >
                <ArrowLeftIcon className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Edit Driver Lead</h1>
                <p className="text-muted-foreground mt-1">
                  Update driver applicant information
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="px-4 lg:px-6">
            <form onSubmit={handleSubmit}>
              <Card>
                <CardHeader>
                  <CardTitle>Driver Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="name">
                        Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Full name"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="email@example.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="source">Source</Label>
                      <Select value={formData.source} onValueChange={(value) => handleChange('source', value)}>
                        <SelectTrigger id="source">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Phone">Phone</SelectItem>
                          <SelectItem value="Indeed">Indeed</SelectItem>
                          <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                          <SelectItem value="Facebook">Facebook</SelectItem>
                          <SelectItem value="Instagram">Instagram</SelectItem>
                          <SelectItem value="Referral">Referral</SelectItem>
                          <SelectItem value="Walk-in">Walk-in</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="source_id">
                        {formData.source === 'Phone' ? 'Phone Number' : 'Social Handle / ID'}
                      </Label>
                      <Input
                        id="source_id"
                        value={formData.source_id}
                        onChange={(e) => handleChange('source_id', e.target.value)}
                        placeholder={
                          formData.source === 'Phone'
                            ? '(555) 123-4567'
                            : formData.source === 'LinkedIn'
                            ? '@username'
                            : 'ID or handle'
                        }
                      />
                    </div>
                  </div>

                  {/* Driver Qualifications */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Driver Qualifications</h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="driver_type">Driver Type</Label>
                        <Select value={formData.driver_type} onValueChange={(value) => handleChange('driver_type', value)}>
                          <SelectTrigger id="driver_type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Company">Company</SelectItem>
                            <SelectItem value="Lease-Owner">Lease-Owner</SelectItem>
                            <SelectItem value="Owner">Owner</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="cdl_status">CDL Status</Label>
                        <Select value={formData.cdl_status} onValueChange={(value) => handleChange('cdl_status', value)}>
                          <SelectTrigger id="cdl_status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Class A">Class A</SelectItem>
                            <SelectItem value="Class B">Class B</SelectItem>
                            <SelectItem value="Class C">Class C</SelectItem>
                            <SelectItem value="None">None</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="years_experience">Years of Experience</Label>
                        <Input
                          id="years_experience"
                          type="number"
                          value={formData.years_experience}
                          onChange={(e) => handleChange('years_experience', e.target.value)}
                          placeholder="0"
                          min="0"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="work_authorization">Work Authorization</Label>
                        <Select value={formData.work_authorization} onValueChange={(value) => handleChange('work_authorization', value)}>
                          <SelectTrigger id="work_authorization">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="US Citizen">US Citizen</SelectItem>
                            <SelectItem value="Green Card">Green Card</SelectItem>
                            <SelectItem value="Work Visa">Work Visa</SelectItem>
                            <SelectItem value="No Authorization">No Authorization</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="currently_working">Currently Working?</Label>
                        <Select value={formData.currently_working} onValueChange={(value) => handleChange('currently_working', value)}>
                          <SelectTrigger id="currently_working">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Yes">Yes</SelectItem>
                            <SelectItem value="No">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="status">Status</Label>
                        <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
                          <SelectTrigger id="status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="application_received">Application Received</SelectItem>
                            <SelectItem value="phone_screen">Phone Screen</SelectItem>
                            <SelectItem value="background_check">Background Check</SelectItem>
                            <SelectItem value="offer_extended">Offer Extended</SelectItem>
                            <SelectItem value="hired">Hired</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      placeholder="Additional notes about the driver applicant..."
                      rows={4}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-4">
                    <Button type="submit">Update Driver Lead</Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => router.visit('/driver_leads')}
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
